import { createFileRoute } from "@tanstack/react-router";
import { loadLocations } from "common/apis/search";
import { buildSearchApiParams } from "@/lib/searchParams";
import { homePinsQueryKey } from "common/hooks/business/home/homePinsQueryKey";
import { searchParamsSchema } from "@/lib/searchParamsSchema";
import { SearchErrorBoundary } from "@/components/Search/SearchErrorBoundary";
import { defaultRegion } from "common/config";
import { en as enLocalizations } from "common/config/localizations";
import { getVenuePageTitle } from "common/utils";
import { venueQueryOptions } from "common/hooks/business/details/venueDetailsQueryOptions";
import type { VenueDetailsBySlugResponse } from "common/hooks/business/details/useVenueDetailsBySlug";
import { sanitiseMetaDescription } from "@/lib/meta";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

export const Route = createFileRoute("/search")({
  ssr: "data-only",
  validateSearch: searchParamsSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search } }) => {
    const params = buildSearchApiParams(search);
    const accessToken = undefined;
    const canonicalOrigin =
      (typeof process !== "undefined" && process.env?.PUBLIC_ORIGIN) || "";

    let venue: VenueDetailsBySlugResponse | null = null;
    if (search.venue) {
      try {
        venue = await context.queryClient.ensureQueryData(
          venueQueryOptions({
            slug: search.venue,
            latitude: defaultRegion.latitude.toString(),
            longitude: defaultRegion.longitude.toString(),
            accessToken: undefined,
          }),
        );
      } catch {
        venue = null;
      }
    }

    try {
      const results = await loadLocations(accessToken, {
        ...params,
        unixTimestamp: Math.floor(Date.now() / 1000),
        include_filter_sections: true,
      });

      context.queryClient.setQueryData(
        homePinsQueryKey(params, accessToken),
        results,
      );

      return {
        results,
        query: search.query || "",
        canonicalOrigin,
        venue,
      };
    } catch {
      return {
        results: null,
        query: search.query || "",
        canonicalOrigin,
        venue,
      };
    }
  },
  head: ({ loaderData }) => {
    const venueDetails = loaderData?.venue?.details;
    const title = venueDetails
      ? getVenuePageTitle(
          {
            name: venueDetails.name,
            route: venueDetails.route,
            sublocality: venueDetails.sublocality,
            postalTown: venueDetails.postalTown,
            locality: venueDetails.locality,
            cityLocation: venueDetails.cityLocation,
          },
          enLocalizations.venuePageTitleSuffix,
        )
      : loaderData?.query
        ? `${enLocalizations.searchResultsFor} "${loaderData.query}" | Sociability`
        : `${enLocalizations.searchResults} | Sociability`;

    const meta: Array<{ title?: string; name?: string; content?: string }> = [
      { title },
    ];
    if (venueDetails) {
      meta.push({
        name: "description",
        content: sanitiseMetaDescription(
          `${enLocalizations.venueMetaDescriptionPrefix} ${venueDetails.name} ${enLocalizations.venueMetaDescriptionSuffix}`,
        ),
      });
    }
    meta.push(
      ...(!isProduction
        ? [{ name: "robots", content: "noindex, nofollow" }]
        : [{ name: "robots", content: "noindex, follow" }]),
    );

    return {
      meta,
      links: [
        {
          rel: "canonical",
          href: `${loaderData?.canonicalOrigin ?? ""}/search`,
        },
      ],
    };
  },
  errorComponent: SearchErrorBoundary,
});
